import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Route, History, AlertTriangle, ExternalLink, Calendar, Users, MapPin } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmt = (f) => (f ? f.split("-").reverse().join("/") : "—");
const fmtCorto = (f) => (f ? f.split("-").slice(1).reverse().join("/") : "—");
const ESTADO_RUTA = { planificada: "Planificada", en_curso: "En curso", finalizada: "Finalizada", cancelada: "Cancelada" };
const ESTADO_CLS = {
  planificada: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  en_curso: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  finalizada: "bg-zinc-700/40 text-zinc-300 border-zinc-600",
  cancelada: "bg-red-500/15 text-red-300 border-red-500/40",
};

const resumen = (r) => `Ruta ${r.codigo || "—"} · ${fmtCorto(r.fecha)}${r.montadores?.length ? ` · Montador: ${r.montadores.join(", ")}` : ""}`;

function RutaCard({ r, lineaId, onAbrir }) {
  return (
    <div data-testid={`ruta-card-${lineaId}-${r.ruta_id}`} className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm text-zinc-100 truncate">{r.codigo || "Sin código"}</div>
          {r.nombre && <div className="text-xs text-zinc-400 truncate">{r.nombre}</div>}
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border shrink-0 ${ESTADO_CLS[r.estado] || ESTADO_CLS.finalizada}`}>
          {ESTADO_RUTA[r.estado] || r.estado}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-xs text-zinc-300">
        <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-zinc-500" />{fmt(r.fecha)}</span>
        <span className="inline-flex items-center gap-1.5 truncate"><Users className="w-3.5 h-3.5 text-zinc-500" />{r.montadores?.length ? r.montadores.join(", ") : "Sin montador"}</span>
        <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-zinc-500" />
          {typeof r.parada_idx === "number" ? `Parada ${r.parada_idx + 1}${r.total_paradas ? ` de ${r.total_paradas}` : ""}` : "Parada —"}
        </span>
      </div>
      <button type="button" onClick={() => onAbrir(r.ruta_id)} data-testid={`abrir-ruta-btn-${lineaId}-${r.ruta_id}`}
        className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white transition-colors">
        <ExternalLink className="w-3.5 h-3.5" /> Abrir ruta
      </button>
    </div>
  );
}

function PlanificacionModal({ open, onClose, info, lineaId, lineaRef }) {
  const navigate = useNavigate();
  const [verHist, setVerHist] = useState(false);
  const activas = info?.activas || [];
  const historicas = info?.historicas || [];
  const abrir = (id) => { onClose(); navigate(`/rutas/${id}`); };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-zinc-950 border-zinc-800" data-testid={`planificacion-modal-${lineaId}`}>
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2"><Route className="w-4 h-4 text-indigo-400" />Planificación de la línea{lineaRef ? <span className="text-zinc-500 font-mono text-sm">· Ref. {lineaRef}</span> : null}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {info?.multiples_activas && (
            <div data-testid={`anomalia-multiples-rutas-${lineaId}`} className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs p-3 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span><b>Anomalía:</b> esta línea aparece en {activas.length} rutas activas a la vez. Revisa y deja solo una; no se ha escogido ninguna automáticamente.</span>
            </div>
          )}
          {activas.length === 0 && (
            <div className="text-xs text-zinc-400 rounded-lg border border-zinc-800 p-3" data-testid={`sin-ruta-activa-${lineaId}`}>Esta línea no está en ninguna ruta activa actualmente.</div>
          )}
          {activas.map((r) => <RutaCard key={r.ruta_id} r={r} lineaId={lineaId} onAbrir={abrir} />)}
          {historicas.length > 0 && (
            <div>
              <button type="button" onClick={() => setVerHist((v) => !v)} data-testid={`toggle-historico-${lineaId}`}
                className="text-xs text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1.5 transition-colors">
                <History className="w-3.5 h-3.5" /> Histórico ({historicas.length}) {verHist ? "▲" : "▼"}
              </button>
              {verHist && <div className="mt-2 space-y-2">{historicas.map((r) => <RutaCard key={r.ruta_id} r={r} lineaId={lineaId} onAbrir={abrir} />)}</div>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Badge compacto «EN RUTA» (puerta de entrada): tooltip con resumen y modal con detalle de planificación. */
export default function EnRutaBadge({ info, lineaId, lineaRef }) {
  const [open, setOpen] = useState(false);
  if (!info || (!info.activas?.length && !info.historicas?.length)) return null;
  const base = "text-[10px] uppercase tracking-wider px-2 py-1 rounded border inline-flex items-center gap-1 font-bold hover:brightness-125 transition-[filter] cursor-pointer";
  let cls, label, icon, testid, tip;
  if (info.multiples_activas) {
    cls = "bg-amber-500/15 text-amber-200 border-amber-500/40"; icon = <AlertTriangle className="w-3 h-3" />;
    label = `En ruta ⚠ (${info.activas.length})`; testid = `en-ruta-multiples-${lineaId}`;
    tip = info.activas.map(resumen);
  } else if (info.actual) {
    cls = "bg-indigo-500/15 text-indigo-300 border-indigo-500/30"; icon = <Route className="w-3 h-3" />;
    label = "En ruta"; testid = `en-ruta-badge-${lineaId}`;
    tip = [resumen(info.actual)];
  } else {
    cls = "bg-zinc-700/30 text-zinc-400 border-zinc-700"; icon = <History className="w-3 h-3" />;
    label = "Histórico"; testid = `ruta-historica-badge-${lineaId}`;
    tip = info.historicas.slice(0, 3).map((h) => `${resumen(h)} · ${ESTADO_RUTA[h.estado] || h.estado}`);
  }
  return (
    <>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" onClick={() => setOpen(true)} data-testid={testid} className={`${base} ${cls}`}>{icon} {label}</button>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs" data-testid={`en-ruta-tooltip-${lineaId}`}>
            {tip.map((t, i) => <div key={i}>{t}</div>)}
            <div className="text-zinc-500 mt-1">Click para ver detalle</div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {open && <PlanificacionModal open={open} onClose={() => setOpen(false)} info={info} lineaId={lineaId} lineaRef={lineaRef} />}
    </>
  );
}
